import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus } from 'react-icons/fa';
import { 
  Box, 
  Typography
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainSection } from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import ConfirmationModal from '../../common/ConfirmationModal';
import ImportTable from '../../common/DataImport/ImportTable';
import { useAlert } from '../../../contexts/AlertContext';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import projectTypePolicyConfig from './config';

const ProjectTypePolicies = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [showControls, setShowControls] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, policy: null });
  const [fieldContext, setFieldContext] = useState({});
  const [expandedErrorPanels, setExpandedErrorPanels] = useState([]);

  // Load data using the config helper
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, fieldContext: context } = await projectTypePolicyConfig.helpers.loadData();
      setPolicies(data);
      setFieldContext(context);
    } catch (error) {
      showAlert('Failed to load project type policies.', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle cell value changes with immediate save
  const handleCellEdit = useCallback(async (id, field, value) => {
    try {
      const item = policies.find(p => p.id === id);
      if (!item) return;

      // Optimistically update the UI with validation status
      const updatedPolicy = { ...item, [field]: value };
      const isValid = updatedPolicy.orgUnitTypeId && updatedPolicy.projectPurposeId;
      updatedPolicy.validationStatus = isValid ? 'valid' : 'invalid';
      updatedPolicy.validationErrors = isValid ? [] : [
        { field: 'general', message: 'Both Organizational Unit Type and Project Purpose are required' }
      ];
      
      setPolicies(prev => prev.map(policy => 
        policy.id === id ? updatedPolicy : policy
      ));

      // Only save to backend if both required fields are present
      if (updatedPolicy.orgUnitTypeId && updatedPolicy.projectPurposeId) {
        // Save using config helper
        const saveResult = await projectTypePolicyConfig.helpers.saveItem(updatedPolicy, field, value);
        
        // Handle response format
        const updatedItem = saveResult.updatedItem || saveResult;
        
        if (saveResult.shouldUpdateDefaults && saveResult.allPolicies) {
          // If we updated the isDefault field, replace all policies with fresh data from server
          setPolicies(saveResult.allPolicies);
          showAlert('Policy updated successfully. Default values refreshed for all policies.', 'success');
        } else {
          // For other fields, just update the specific item
          setPolicies(prev => prev.map(policy => 
            policy.id === id ? updatedItem : policy
          ));
          showAlert('Policy updated successfully', 'success');
        }
      }
    } catch (error) {
      // Revert the change on error
      await loadData();
      showAlert('Failed to update policy', 'error', error);
    }
  }, [policies, loadData, showAlert]);

  const handleAddPolicy = async () => {
    try {
      const newItem = projectTypePolicyConfig.helpers.addNewItem();
      setPolicies(prev => [...prev, newItem]);
      showAlert('New policy row added', 'success');
    } catch (error) {
      showAlert('Failed to add policy', 'error', error);
    }
  };

  const handleDeletePolicy = async () => {
    if (!deleteModal.policy) return;
    
    try {
      await projectTypePolicyConfig.helpers.deleteItem(deleteModal.policy);
      setPolicies(prev => prev.filter(p => p.id !== deleteModal.policy.id));
      showAlert('Policy deleted successfully', 'success');
    } catch (error) {
      showAlert('Failed to delete policy', 'error', error);
    } finally {
      setDeleteModal({ open: false, policy: null });
    }
  };

  // Permission check functions for ImportTable
  const canEdit = useCallback(() => {
    return ability.can(ACTIONS.UPDATE, SUBJECTS.PROJECT_TYPE_POLICY);
  }, [ability]);

  const canDelete = useCallback(() => {
    return ability.can(ACTIONS.DELETE, SUBJECTS.PROJECT_TYPE_POLICY);
  }, [ability]);

  // Handle error panel toggle
  const handleToggleErrorPanel = useCallback((itemId) => {
    setExpandedErrorPanels(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const config = projectTypePolicyConfig;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/')}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Project Type Policies</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              {config.documentation.description}
            </Typography>
            {ability.can(ACTIONS.CREATE, SUBJECTS.PROJECT_TYPE_POLICY) && (
              <HeaderButton onClick={handleAddPolicy}>
                <FaPlus /> Add Policy
              </HeaderButton>
            )}
          </ControlsContainer>

          <MainSection $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px',
              backgroundColor: '#F3F4F6',
              borderRadius: '8px'
            }}>
              <Box sx={{ backgroundColor: 'white', borderRadius: '4px', overflow: 'hidden' }}>
                <ImportTable
                  config={config}
                  data={policies}
                  loading={isLoading}
                  editingCell={editingCell}
                  isSelectOpen={isSelectOpen}
                  onCellEdit={handleCellEdit}
                  onDeleteItem={(id) => {
                    const policy = policies.find(p => p.id === id);
                    if (policy && canDelete()) {
                      setDeleteModal({ open: true, policy });
                    }
                  }}
                  onToggleErrorPanel={handleToggleErrorPanel}
                  expandedErrorPanels={expandedErrorPanels}
                  fieldContext={fieldContext}
                  onSelectOpen={() => setIsSelectOpen(true)}
                  onSelectClose={() => {
                    setIsSelectOpen(false);
                    setTimeout(() => setEditingCell(null), 250);
                  }}
                  onStartEdit={(id, field) => {
                    if (canEdit()) {
                      setEditingCell({ id, field });
                    }
                  }}
                  onStopEdit={() => setEditingCell(null)}
                  showDeleteButton={true}
                  deleteButtonTitle="Delete policy"
                  emptyStateMessage="No project type policies found. Click 'Add Policy' to create the first one."
                  emptyStateColSpan={config.tableColumns.length + 2}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              </Box>
            </div>
          </MainSection>
        </div>
      </Content>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, policy: null })}
        onConfirm={handleDeletePolicy}
        title="Delete Project Type Policy"
        message="Are you sure you want to delete this policy? This action cannot be undone."
      />
    </Container>
  );
};

export default ProjectTypePolicies;